import { useEffect, useMemo, useState } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { useI18n } from '../../bootstrap/i18n';
import { navigate } from '../../router/router';
import { IconChat, IconClose, IconStar, IconTelegram } from '../../ui/icons';
import { BottomSheet } from '../../ui/sheets/BottomSheet';
import { apiJsonOrThrow, authedFetchJson, getAccessTokenOrNull } from '../../utils/api';
import { requestAuthRequired } from '../../utils/telegramUser';
import { normalizeAssetUrl } from '../../utils/urls';
import { toThumbUrl } from '../../utils/thumbUrl';

type EntryModel = {
  id: number;
  name: string;
  topic?: string | null;
  image_url?: string | null;
};

type EntryOptions = {
  ok: true;
  model: { id: number; name: string; photo_url?: string | null };
  entry_modal?: { enabled: boolean };
  private_show?: {
    enabled: boolean;
    price_credits: number;
    message_price_credits?: number;
    command_price_credits?: number;
    user_balance?: number | null;
  };
  direct_telegram?: {
    enabled: boolean;
    requires_follow: boolean;
    follow_configured: boolean;
    transport?: string | null;
    username?: string | null;
    open_url?: string | null;
    direct_url?: string | null;
  };
  follow_gate?: {
    enabled: boolean;
    configured: boolean;
    channel_url?: string | null;
    channel_id?: string | null;
    status?: string;
  };
  instagram?: {
    enabled: boolean;
    url?: string | null;
  };
};

type VerifyResult = {
  open_url?: string | null;
  instagram_url?: string | null;
};

function makeClientEventId() {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {}
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function modelThumb(url?: string | null) {
  const normalized = normalizeAssetUrl(url);
  if (!normalized) return null;
  return toThumbUrl(normalized, { cssWidth: 144, cssHeight: 144 });
}

function modelHeroPhoto(url?: string | null) {
  const normalized = normalizeAssetUrl(url);
  if (!normalized) return null;
  return toThumbUrl(normalized, { cssWidth: 420, cssHeight: 560 });
}

function openExternalUrl(services: ReturnType<typeof bootOnce>, url: string) {
  const u = String(url || '').trim();
  if (!u) return false;
  const tg = services.telegram.tg;
  const isTelegramUrl = /^https?:\/\/(t\.me|telegram\.me)\//i.test(u);
  if (isTelegramUrl && tg?.openTelegramLink) tg.openTelegramLink(u);
  else if (tg?.openLink) tg.openLink(u);
  else window.open(u, '_blank', 'noopener,noreferrer');
  return true;
}

function buildTelegramUsernameUrl(username?: string | null) {
  const handle = String(username || '').trim().replace(/^@+/, '').split(/[/?#]/)[0];
  return handle ? `https://t.me/${encodeURIComponent(handle)}` : '';
}

function directTelegramUrlFromOptions(options: EntryOptions | null) {
  const direct = options?.direct_telegram;
  if (!direct?.enabled || direct.requires_follow) return '';
  return String(direct.open_url || direct.direct_url || '').trim() || buildTelegramUsernameUrl(direct.username);
}

export function ModelEntrySheet(props: {
  model: EntryModel;
  onClose: () => void;
  onOpenTopup?: () => void;
}) {
  const services = useMemo(() => bootOnce(), []);
  const { t } = useI18n(services.i18n);
  const [options, setOptions] = useState<EntryOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'private' | 'direct' | 'verify' | null>(null);
  const [error, setError] = useState<string>('');
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [telegramFlowOpen, setTelegramFlowOpen] = useState(false);

  const basePhotoRaw = options?.model?.photo_url || props.model.image_url;
  const photoUrl = modelThumb(basePhotoRaw);
  const heroPhotoUrl = modelHeroPhoto(basePhotoRaw);
  const modelName = options?.model?.name || props.model.name;
  const modelTopic = String(props.model.topic || '').trim();
  const priceCredits = Math.max(0, Number(options?.private_show?.price_credits || 0) || 0);
  const messagePriceCredits = Math.max(0, Number(options?.private_show?.message_price_credits || 0) || 0);
  const commandPriceCredits = Math.max(0, Number(options?.private_show?.command_price_credits || 0) || 0);
  const creditLabel = (n: number) => `${n} ${n === 1 ? t('billing.credit', 'credit') : t('billing.credits', 'credits')}`;
  const privatePricingText = (messagePriceCredits > 0 || commandPriceCredits > 0)
    ? [
        `${t('entry.messages', 'Messages')} ${messagePriceCredits > 0 ? creditLabel(messagePriceCredits) : t('private_show.included', 'Included')}`,
        `${t('entry.actions', 'Actions')} ${commandPriceCredits > 0 ? creditLabel(commandPriceCredits) : t('private_show.included', 'Included')}`,
      ].join(' | ')
    : t('entry.private_included', 'Messages and actions included');
  const showFollowGate = Boolean(options?.follow_gate?.enabled);
  const followConfigured = options?.follow_gate?.configured !== false;
  const canOpenTelegram = Boolean(options?.direct_telegram?.enabled);
  const channelUrl = String(options?.follow_gate?.channel_url || '').trim();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setVerifyResult(null);
    setTelegramFlowOpen(false);
    (async () => {
      try {
        const token = await getAccessTokenOrNull(services, { timeoutMs: 800 });
        const data = await apiJsonOrThrow<EntryOptions>(
          `/api/telegram-entry/options?model_id=${encodeURIComponent(String(props.model.id))}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
          { feature: 'storefront', endpoint: '/api/telegram-entry/options', modelId: props.model.id },
        );
        if (cancelled) return;
        if ((data as EntryOptions)?.entry_modal?.enabled === false) {
          props.onClose();
          navigate({ name: 'chat', modelId: String(props.model.id) });
          return;
        }
        setOptions(data as EntryOptions);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || t('entry.error_loading', 'Could not load options'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [props.model.id]);

  async function openPrivateShow() {
    if (busy) return;
    setError('');
    setBusy('private');
    try {
      const result = await authedFetchJson<any>(services, '/api/telegram-entry/private-show/open', {
        method: 'POST',
        body: JSON.stringify({
          model_id: props.model.id,
          idempotency_key: makeClientEventId(),
        }),
      }, {
        ctx: { feature: 'storefront', stage: 'entry_private_show', modelId: props.model.id },
      });
      const data = (result as any)?.data || {};
      if ((result as any)?.ok && data?.ok !== false) {
        props.onClose();
        navigate({ name: 'chat', modelId: String(props.model.id) });
        return;
      }
      if (data?.reason === 'insufficient_credits') {
        setError(t('entry.insufficient', 'Not enough credits for this private show.'));
        return;
      }
      setError(String(data?.message || data?.error || t('entry.private_failed', 'Could not start private show')));
    } catch (e: any) {
      try { requestAuthRequired({ path: window.location.pathname || '/' }); } catch {}
      setError(e?.message || t('entry.auth_required', 'Open in Telegram to continue.'));
    } finally {
      setBusy(null);
    }
  }

  async function recordDirectTelegramOpen() {
    try {
      await authedFetchJson<any>(services, '/api/telegram-entry/direct-open', {
        method: 'POST',
        body: JSON.stringify({
          model_id: props.model.id,
          source: 'entry_modal_direct',
          client_event_id: makeClientEventId(),
        }),
      }, {
        ctx: { feature: 'storefront', stage: 'entry_direct_open', modelId: props.model.id },
      });
    } catch {}
  }

  async function openDirectTelegramFallback() {
    if (busy) return;
    setError('');
    setBusy('direct');
    try {
      const result = await authedFetchJson<any>(services, '/api/telegram-entry/direct-open', {
        method: 'POST',
        body: JSON.stringify({
          model_id: props.model.id,
          source: 'entry_modal_direct',
          client_event_id: makeClientEventId(),
        }),
      }, {
        ctx: { feature: 'storefront', stage: 'entry_direct_open', modelId: props.model.id },
      });
      const data = (result as any)?.data || {};
      if ((result as any)?.ok && data?.ok !== false && data?.open_url) {
        openExternalUrl(services, String(data.open_url));
        props.onClose();
        return;
      }
      if (data?.reason === 'follow_required') {
        setError(t('entry.follow_required', 'Open her channel first.'));
        return;
      }
      setError(String(data?.message || data?.error || t('entry.direct_failed', 'Could not open Telegram chat')));
    } catch (e: any) {
      try { requestAuthRequired({ path: window.location.pathname || '/' }); } catch {}
      setError(e?.message || t('entry.auth_required', 'Open in Telegram to continue.'));
    } finally {
      setBusy(null);
    }
  }

  async function verifyFollowGate() {
    if (busy) return;
    setError('');
    setBusy('verify');
    try {
      const result = await authedFetchJson<any>(services, '/api/telegram-entry/follow-gate/verify', {
        method: 'POST',
        body: JSON.stringify({
          model_id: props.model.id,
          client_event_id: makeClientEventId(),
        }),
      }, {
        ctx: { feature: 'storefront', stage: 'entry_follow_verify', modelId: props.model.id },
      });
      const data = (result as any)?.data || {};
      if ((result as any)?.ok && data?.ok !== false) {
        setVerifyResult({
          open_url: data.open_url || null,
          instagram_url: data.instagram_url || options?.instagram?.url || null,
        });
        return;
      }
      if (data?.reason === 'not_subscribed') {
        setError(t('entry.not_subscribed', 'We cannot see the channel join yet. Open it, then check again.'));
        return;
      }
      if (data?.reason === 'telegram_required') {
        setError(t('entry.telegram_required', 'Telegram account is required for this check.'));
        return;
      }
      setError(String(data?.message || data?.error || t('entry.verify_failed', 'Could not verify subscription')));
    } catch (e: any) {
      try { requestAuthRequired({ path: window.location.pathname || '/' }); } catch {}
      setError(e?.message || t('entry.auth_required', 'Open in Telegram to continue.'));
    } finally {
      setBusy(null);
    }
  }

  function startTelegramFlow() {
    if (busy) return;
    setError('');
    if (showFollowGate) {
      setTelegramFlowOpen(true);
      return;
    }
    const immediateUrl = directTelegramUrlFromOptions(options);
    if (immediateUrl) {
      openExternalUrl(services, immediateUrl);
      props.onClose();
      void recordDirectTelegramOpen();
      return;
    }
    void openDirectTelegramFallback();
  }

  const privateDisabled = loading || busy !== null || options?.private_show?.enabled === false;
  const directDisabled = loading || busy !== null || !canOpenTelegram;
  const verifyDisabled = loading || busy !== null || !followConfigured || !channelUrl;

  return (
    <BottomSheet
      open={true}
      onClose={props.onClose}
      ariaLabel={t('entry.title', 'Choose how to continue')}
      sheetClassName="entrySheet"
    >
      <button class="entryCloseBtn" type="button" aria-label={t('btn.close', 'Close')} onClick={props.onClose}>
        <IconClose size={18} />
      </button>

      <div class="entryHeader">
        {heroPhotoUrl ? (
          <div class="entryHeroPhoto">
            <img src={heroPhotoUrl} alt="" loading="eager" decoding="async" />
          </div>
        ) : (
          <div class="entryAvatar">
            {photoUrl ? <img src={photoUrl} alt="" /> : <span>{modelName.slice(0, 1).toUpperCase()}</span>}
          </div>
        )}
        <div class="entryHeroText">
          <div class="entryTitle">{modelName}</div>
          {modelTopic ? <div class="entryDescription">{modelTopic}</div> : null}
        </div>
      </div>

      <div class="entryOptions">
        <button class="entryOption entryPrivateOption" disabled={privateDisabled} onClick={openPrivateShow}>
          <span class="entryOptionIcon"><IconChat size={22} /></span>
          <span class="entryOptionBody">
            <span class="entryOptionTitle">{t('entry.private_title', 'Visit private show')}</span>
            <span class="entryOptionText">{privatePricingText}</span>
          </span>
          <span class="entryPrice">
            {priceCredits > 0 ? (
              <>
                {priceCredits}
                <IconStar size={15} filled />
              </>
            ) : t('entry.free', 'Free')}
          </span>
        </button>

        <button
          class={`entryOption entryTelegramBlock ${telegramFlowOpen ? 'active' : ''}`}
          disabled={directDisabled}
          onClick={startTelegramFlow}
          aria-pressed={telegramFlowOpen}
        >
          <span class="entryOptionIcon"><IconTelegram size={24} /></span>
          <span class="entryOptionBody">
            <span class="entryOptionTitle">{t('entry.telegram_title', 'Go to Telegram')}</span>
            <span class="entryOptionText">
              {showFollowGate
                ? t('entry.telegram_follow_text', 'Telegram route with direct chat access')
                : t('entry.telegram_direct_text', 'Open her direct Telegram chat')}
            </span>
          </span>
          <span class="entryFreeBadge">{t('entry.free_badge', 'FREE')}</span>
        </button>

        {telegramFlowOpen && showFollowGate ? (
          <div class="entryTelegramFlow">
            <div class="entryFlowText">
              {t('entry.telegram_flow_text', 'Telegram access opens in two quick steps.')}
            </div>
            <div class="entryStep">
              <span class="entryStepNumber">1</span>
              <span class="entryStepBody">
                <span class="entryStepTitle">{t('entry.step_subscribe_title', 'Open her channel')}</span>
                <span class="entryStepText">{t('entry.step_subscribe_text', 'Telegram will open in a new view.')}</span>
              </span>
              <button class="secondaryBtn entryStepBtn" disabled={!channelUrl || busy !== null} onClick={() => openExternalUrl(services, channelUrl)}>
                {t('entry.subscribe', 'Open channel')}
              </button>
            </div>
            <div class="entryStep">
              <span class="entryStepNumber">2</span>
              <span class="entryStepBody">
                <span class="entryStepTitle">{t('entry.step_confirm_title', 'Come back to unlock')}</span>
                <span class="entryStepText">{t('entry.step_confirm_text', 'We will check access and show the direct chat button.')}</span>
              </span>
              <button class="primaryBtn entryStepBtn" disabled={verifyDisabled} onClick={verifyFollowGate}>
                {busy === 'verify' ? t('entry.checking', 'Checking...') : t('entry.i_subscribed', 'I joined')}
              </button>
            </div>
          </div>
        ) : null}

        {verifyResult ? (
          <div class="entryUnlocked">
            <button class="primaryBtn entryWideBtn" onClick={() => verifyResult.open_url ? openExternalUrl(services, verifyResult.open_url) : null}>
              {t('entry.open_telegram', 'Open Telegram chat')}
            </button>
            {verifyResult.instagram_url ? (
              <button class="secondaryBtn entryWideBtn" onClick={() => verifyResult.instagram_url ? openExternalUrl(services, verifyResult.instagram_url) : null}>
                {t('entry.open_instagram', 'Instagram')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? (
        <div class="entryError">
          <span>{error}</span>
          {error === t('entry.insufficient', 'Not enough credits for this private show.') && props.onOpenTopup ? (
            <button class="secondaryBtn" onClick={props.onOpenTopup}>{t('btn.topup', 'Top Up Balance')}</button>
          ) : null}
        </div>
      ) : null}
    </BottomSheet>
  );
}
